import {
	AssetTreePicker,
	ErrorModal,
	useUserContext,
} from "@dexteel/mesf-core";
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	IconButton,
	Paper,
	Snackbar,
	Typography,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import AddBoxIcon from "@mui/icons-material/AddBox";
import FormatListBulletedSharpIcon from "@mui/icons-material/FormatListBulletedSharp";
import { Alert } from "@mui/lab";
import { globalDefaultColDef } from "@setup/ag-grid/mesf-theme";
import type {
	ColDef,
	DefaultMenuItem,
	EditableCallbackParams,
	GetContextMenuItemsParams,
	ICellRendererParams,
	MenuItemDef,
} from "ag-grid-community";
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import type React from "react";
import {
	type SyntheticEvent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import CustomLoadingOverlay from "../../components/loaders/LazyLoading/LazyLoading";
import { AssetNameColumnColumn } from "../work-orders/components/assetName-column";
import { CreateTemplate } from "./components/CreateTemplate";
import { EditTemplate } from "./components/EditTemplate";
import { Filters } from "./components/filters";
import {
	type ResourceType,
	TableTemplateResoures,
} from "./components/template-resources/table-template-resoures";
import { useTemplatesContext } from "./context/templatesContext";
import { useInitializeTemplate } from "./hooks/useInitializeTemplate";
import { useLoadPeriodPerDay } from "./hooks/useLoadPeriodPerDay";
import { useSearchData } from "./hooks/useSearchData";
import { Template } from "./models/Template";
import { getCraftLetter } from "@/utils/craftLetter";
import {
	createPMOrder,
	deleteTemplate,
} from "./repositories/TemplatesRepositorie";

export const Templates = () => {
	const {
		state: { defaultAreaId },
	} = useUserContext();
	const {
		state: {
			rowData,
			searchAssetIdToTable,
			templateIdToDelete,
			rowSelectedTemplate,
			rowSelectedTemplate: { AssetId, TemplateId, Title, AssetName, TypeCode },
			rows,
		},
		actions: {
			setTemplateIdToDelete,
			setSelectedTemplateId,
			setSelectedAssetId,
			setSelectedTitle,
			setSelectedCraftId,
			setSelectedDownHours,
			setSelectedPlannedHours,
			setSelectedDowntimeClassCode,
			setSelectedNumberOfWorkers,
			setSelectedDescription,
			setSelectedExpectedShifts,
			setSelectedWarningShifts,
			setSelectedShiftsPerDay,
			setSelectedPeriodDays,
			setSelectedWarningDays,
			setSelectedExpectedDays,
			setSelectedAssetName,
			setSelectedSchedulingPolicyCode,
			setSelectedScheduleStart,
			setFirstTime,
			setRowSelectedTemplate,
			setSelectedCronExpression,
			setSelectedNotifyOnGeneration,
		},
	} = useTemplatesContext();

	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const gridRef = useRef<AgGridReact>(null);
	const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [error, setError] = useState<string>("");
	const [isParametersLoading, setIsParametersLoading] = useState<boolean>(true);
	const [parametersError, setParametersError] = useState<string>("");
	const [createPMOrderError, setCreatePMOrderError] = useState<string>("");
	const [deleteTemplateError, setDeleteTemplateError] = useState<string>("");

	const loadPeriodPerDay = useLoadPeriodPerDay({
		setIsParametersLoading,
		setParametersError,
	});

	const searchData = useSearchData({ setIsLoading, setError });

	const [modalCreateActive, setModalCreateActive] = useState(false);
	// Row the create modal copies its values from; null for a blank new template
	const [duplicateSource, setDuplicateSource] = useState<Template | null>(null);
	const [modalEditActive, setModalEditActive] = useState(false);
	const [onDemandModal, setOnDemandModal] = useState(false);
	const [openModalDelete, setopenModalDelete] = useState<boolean>(false);
	const [deleteSuccess, setDeleteSuccess] = useState<boolean>(false);
	const [openTemplateResources, setOpenTemplateResources] = useState(false);
	const [selectedResource, setSelectedResource] = useState<ResourceType | null>(
		null,
	);
	const [newMode, setNewMode] = useState(false);

	const openResourceModal = useCallback(
		(resource: ResourceType, rowData: any) => {
			setRowSelectedTemplate(rowData);
			setSelectedResource(resource);
			setOpenTemplateResources(true);
		},
		[setRowSelectedTemplate],
	);

	const customPeriodRenderer = (params: ICellRendererParams) => {
		const { data } = params;
		const schedulingPolicyCode = data.SchedulingPolicyCode;
		if (schedulingPolicyCode === "P") {
			return "Periodic";
		} else {
			return data.PeriodInDays;
		}
	};

	const onActionsClick = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		params: EditableCallbackParams,
	) => {
		(params.api as any).showContextMenu({
			column: params.column,
			rowNode: params.node,
			undefined,
			mouseEvent: e.nativeEvent,
		});
	};

	const renderCategoryCell = (props: ICellRendererParams) => {
		return (
			<div
				style={{
					height: 32,
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
				}}
			>
				<div
					style={{
						height: 26,
						width: 26,
						borderRadius: 4,
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						background: props.data?.CraftColor || "#FFFFFF",
					}}
				>
					<div style={{ color: "#FFF", fontWeight: 600 }}>
						<div>
							{getCraftLetter(props.data?.CraftName, props.data?.CraftCode)}
						</div>
					</div>
				</div>
			</div>
		);
	};

	const [columnDefs] = useState<ColDef[]>([
		{
			cellRenderer: AssetNameColumnColumn,
			headerName: "Asset",
			minWidth: 290,
			flex: 3,
		},
		{
			field: "Title",
			headerName: "Title",
			minWidth: 130,
			flex: 4,
		},
		{
			field: "CraftId",
			headerName: "Craft",
			minWidth: 80,
			flex: 1,
			cellRenderer: renderCategoryCell,
		},
		{
			field: "PeriodInDays",
			headerName: "Period (days)",
			minWidth: 120,
			flex: 1,
			cellRenderer: customPeriodRenderer,
		},
		{
			field: "PlannedHours",
			headerName: "Planned Hours",
			maxWidth: 120,
			flex: 1,
		},
		{
			field: "NumberOfWorkers",
			headerName: "# Of Workers",
			minWidth: 120,
			flex: 1,
		},
		{
			headerName: "",
			flex: 1,
			maxWidth: 80,
			cellClass: "ag-columns",
			cellRenderer: (params: EditableCallbackParams) => {
				return (
					<Grid2
						container
						style={{
							height: "100%",
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						<Grid2
							style={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
							}}
							size={{ xs: 12, md: 12 }}
						>
							<Grid2
								container
								spacing={1}
								style={{ justifyContent: "flex-end", minWidth: 30 }}
							>
								<Grid2 size={{ xs: 12, md: 12 }}>
									<Button fullWidth onClick={(e) => onActionsClick(e, params)}>
										<FormatListBulletedSharpIcon
											style={{ height: "auto" }}
											color="action"
										/>
									</Button>
								</Grid2>
							</Grid2>
						</Grid2>
					</Grid2>
				);
			},
		},
	]);

	const handleClose = (
		event: Event | React.SyntheticEvent,
		reason?: string,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setDeleteSuccess(false);
	};

	const loadingOverlayComponent = useMemo<any>(() => {
		return CustomLoadingOverlay;
	}, []);

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const rowSelected = params.node?.data;
			rowSelected && setRowSelectedTemplate(rowSelected);
			const result: (DefaultMenuItem | MenuItemDef)[] = [
				{
					name: "New Template",
					action: () => {
						setModalCreateActive(true);
					},
					icon: '<i class="fas fa-plus"></i>',
				},
			];
			if (params.node?.data) {
				result.push({
					name: "Duplicate Template",
					action: () => {
						setDuplicateSource(params.node?.data);
						setModalCreateActive(true);
					},
					icon: '<i class="fas fa-copy"></i>',
				});
			}
			if (params.node?.data) {
				result.push({
					name: "Asset Details",
					action: () => {
						navigate(
							`/asset-details/${params.node?.data?.AssetId}?resource=drawings&assets=true&statistics=true&templates=true`,
						);
					},
					icon: '<i class="fas fa-file-alt"></i>',
				});
			}
			result.push(
				{
					name: "Template Documents",
					action: () => openResourceModal("DOCUMENTS", params.node?.data),
					icon: '<i class="far fa-file-image"></i>',
				},
				{
					name: "Template Attachments",
					action: () => openResourceModal("ATTACHMENTS", params.node?.data),
					icon: '<i class="fas fa-paperclip"></i>',
				},
				{
					name: "Template LOTO",
					action: () => openResourceModal("LOTO", params.node?.data),
					icon: '<i class="fas fa-tag"></i>',
				},
				{
					name: "Template Parts",
					action: () => openResourceModal("PARTS", params.node?.data),
					icon: '<i class="fas fa-tools"></i>',
				},
			);

			if (params.node?.data) {
				result.push({
					name: "Edit Template",
					action: () => {
						setSelectedTemplateId(params.node?.data.TemplateId),
							setSelectedAssetId(params.node?.data.AssetId),
							setSelectedAssetName(params.node?.data.AssetName),
							setSelectedTitle(params.node?.data.Title),
							setSelectedCraftId(params.node?.data.CraftId),
							setSelectedDownHours(params.node?.data.DownHours),
							setSelectedPlannedHours(params.node?.data.PlannedHours),
							setSelectedDowntimeClassCode(params.node?.data.DowntimeClassCode),
							setSelectedNumberOfWorkers(params.node?.data.NumberOfWorkers),
							setSelectedDescription(params.node?.data.Description),
							setSelectedExpectedShifts(params.node?.data.ExpectedShifts),
							setSelectedWarningShifts(params.node?.data.WarningShifts),
							setSelectedShiftsPerDay(params.node?.data.shiftsPerDay),
							setSelectedPeriodDays(params.node?.data.PeriodShifts),
							setSelectedWarningDays(params.node?.data.WarningShifts),
							setSelectedExpectedDays(params.node?.data.ExpectedShifts),
							setSelectedSchedulingPolicyCode(
								params.node?.data.SchedulingPolicyCode,
							),
							setSelectedCronExpression(params.node?.data.CronExpression),
							setSelectedNotifyOnGeneration(
								params.node?.data.NotifyOnGeneration,
							),
							setSelectedScheduleStart(params.node?.data.ScheduleStart);
						setModalEditActive(true);
					},
					icon: '<i class="fas fa-edit"></i>',
				});
			}
			if (params.node?.data) {
				result.push({
					name: "Delete Template",
					action: () => {
						setopenModalDelete(true);
						setTemplateIdToDelete(rowSelected.TemplateId);
					},
					icon: '<i class="fas fa-trash"></i>',
				});
			}
			if (params.node?.data) {
				result.push("separator", {
					name: "Force Open Order",
					action: () => setOnDemandModal(true),
					icon: '<i class="far fa-plus-square"></i>',
				});
			}
			return result;
		},
		[rowData],
	);

	const onDobleClicked = () => {
		const selectedRow = gridRef.current!.api.getSelectedNodes();
		setRowSelectedTemplate(selectedRow[0].data);
		setSelectedTemplateId(selectedRow[0].data.TemplateId),
			setSelectedAssetId(selectedRow[0].data.AssetId),
			setSelectedAssetName(selectedRow[0].data.AssetName),
			setSelectedTitle(selectedRow[0].data.Title),
			setSelectedCraftId(selectedRow[0].data.CraftId),
			setSelectedDownHours(selectedRow[0].data.DownHours),
			setSelectedPlannedHours(selectedRow[0].data.PlannedHours),
			setSelectedDowntimeClassCode(selectedRow[0].data.DowntimeClassCode),
			setSelectedNumberOfWorkers(selectedRow[0].data.NumberOfWorkers),
			setSelectedDescription(selectedRow[0].data.Description),
			setSelectedExpectedShifts(selectedRow[0].data.ExpectedShifts),
			setSelectedWarningShifts(selectedRow[0].data.WarningShifts),
			setSelectedShiftsPerDay(selectedRow[0].data.shiftsPerDay),
			setSelectedPeriodDays(selectedRow[0].data.PeriodShifts),
			setSelectedWarningDays(selectedRow[0].data.WarningShifts),
			setSelectedExpectedDays(selectedRow[0].data.ExpectedShifts),
			setSelectedSchedulingPolicyCode(selectedRow[0].data.SchedulingPolicyCode),
			setSelectedScheduleStart(selectedRow[0].data.ScheduleStart),
			setSelectedCronExpression(selectedRow[0].data.CronExpression),
			setSelectedNotifyOnGeneration(selectedRow[0].data.NotifyOnGeneration),
			setModalEditActive(true);
	};

	const handleCreatePMorder = async () => {
		const res = await createPMOrder(rowSelectedTemplate.TemplateId as number);
		if (!res.ok) setCreatePMOrderError(res.message);
		setOnDemandModal(false);
	};

	const changeSearchAsset = (value: number | null) => {
		navigate(`/templates/${value}`, { replace: true });
	};

	const deleteDetail = async (rowSelected: number) => {
		const res = await deleteTemplate(rowSelected);
		if (res.ok) {
			setopenModalDelete(false);
			setDeleteSuccess(true);
			searchData();
		} else setDeleteTemplateError(res.message);
	};

	const resetFilter = () => {
		searchParams.delete("search");
		setSearchParams(searchParams, { replace: true });

		setTimeout(() => {
			setFirstTime(true);
			navigate(`/templates/${defaultAreaId}`, { replace: true });
		}, 500);
	};

	useInitializeTemplate();

	useEffect(() => {
		if (searchAssetIdToTable !== 0) {
			loadPeriodPerDay();
		}
	}, [searchAssetIdToTable]);

	useEffect(() => {
		const templateId = searchParams.get("templateId");
		const templateIdNum = templateId ? Number.parseInt(templateId) : null;
		const templateRow = templateIdNum
			? rows.find((row: Template) => row.TemplateId === templateIdNum)
			: null;

		if (templateRow && gridRef.current) {
			gridRef.current.api.forEachNode((node) => {
				if (node.data?.TemplateId === templateIdNum) {
					node.setSelected(true);
					gridRef.current!.api.ensureNodeVisible(node);
					setRowSelectedTemplate(templateRow);
				}
			});
		}
	}, [searchParams, rows, setRowSelectedTemplate]);

	return (
		<>
			{openTemplateResources && selectedResource && (
				<TableTemplateResoures
					show={openTemplateResources}
					onHide={(shouldUpdate: boolean) => {
						setOpenTemplateResources(false);
						setSelectedResource(null);
						if (shouldUpdate) {
							searchData();
						}
					}}
					initialResource={selectedResource}
					AssetId={AssetId}
					TemplateId={TemplateId}
					Title={Title}
					AssetName={AssetName as string}
					TypeCode={TypeCode as string}
				/>
			)}
			<Grid2
				container
				style={{
					padding: "10px 20px",
					height: "calc(100vh - 64px)",
					boxSizing: "border-box",
					display: "flex",
					flexDirection: "column",
					flexWrap: "nowrap",
				}}
			>
				<Grid2
					container
					size={{ xs: 12 }}
					alignItems="center"
					justifyContent="center"
					spacing={1}
					sx={{ flexShrink: 0, width: "100%" }}
				>
					<Grid2 size={{ md: 12, xs: 12 }}>
						<Typography
							variant="h5"
							style={{
								margin: "0",
								fontWeight: 600,
								userSelect: "none",
								marginLeft: 5,
							}}
						>
							TEMPLATES
						</Typography>
					</Grid2>

					<Grid2 size={{ xs: 12 }}>
						<Paper elevation={1} style={{ padding: 10, marginBottom: 20 }}>
							<Grid2 container spacing={2} alignItems="center">
								<Grid2 size={{ lg: 3, md: 3, sm: 12, xs: 12 }}>
									<AssetTreePicker
										label="Asset"
										value={Number.parseInt(searchAssetIdToTable as string) || 0}
										onChange={changeSearchAsset}
									/>
								</Grid2>

								<Grid2 size={{ lg: 7, md: 7, sm: 12, xs: 12 }}>
									<Filters searchData={searchData} />
								</Grid2>

								<Grid2 size={{ lg: 2, md: 2, sm: 12, xs: 12 }}>
									<Button
										fullWidth
										variant="contained"
										color="inherit"
										onClick={resetFilter}
										size="medium"
									>
										RESET
									</Button>
								</Grid2>
							</Grid2>
						</Paper>
					</Grid2>
				</Grid2>

				<Paper
					elevation={1}
					style={{
						width: "100%",
						padding: 10,
						borderRadius: "0.75rem",
						flex: 1,
						minHeight: 0,
						display: "flex",
						flexDirection: "column",
						boxSizing: "border-box",
					}}
				>
					<Grid2
						style={{
							flex: 1,
							minHeight: 0,
							width: "100%",
							paddingBottom: 60,
							position: "relative",
						}}
						size={{ md: 12, xs: 12 }}
					>
						<div style={gridStyle}>
							<AgGridReact
								ref={gridRef}
								rowData={rows}
								columnDefs={columnDefs}
								defaultColDef={globalDefaultColDef}
								rowHeight={35}
								headerHeight={32}
								animateRows={true}
								loadingOverlayComponent={loadingOverlayComponent}
								getContextMenuItems={getContextMenuItems}
								rowSelection="single"
								onRowDoubleClicked={onDobleClicked}
								loading={isLoading}
							/>
							<IconButton
								onClick={() => {
									setModalCreateActive(!modalCreateActive);
								}}
								aria-label="add"
								color="primary"
								style={{
									padding: 0,
									position: "absolute",
									left: 10,
									bottom: 10,
								}}
							>
								<AddBoxIcon style={{ width: 35, height: 35 }} />
							</IconButton>
						</div>
					</Grid2>
				</Paper>
			</Grid2>

			<CreateTemplate
				show={modalCreateActive}
				duplicateSource={duplicateSource}
				// A new template starts on the asset the page is filtered on
				initialAssetId={
					Number.isNaN(Number.parseInt(searchAssetIdToTable as string, 10))
						? null
						: Number.parseInt(searchAssetIdToTable as string, 10)
				}
				onHide={(shouldUpdate: boolean) => {
					setDuplicateSource(null);
					if (shouldUpdate) {
						searchData();
						setModalCreateActive(false);
					} else setModalCreateActive(false);
				}}
			/>
			<EditTemplate
				show={modalEditActive}
				onHide={(shouldUpdate: boolean) => {
					if (shouldUpdate) {
						searchData();
						setModalEditActive(false);
					} else setModalEditActive(false);
				}}
				data={rowSelectedTemplate}
				initialNewMode={newMode}
			/>

			<Dialog open={openModalDelete} aria-labelledby="responsive-dialog-title">
				<DialogTitle id="responsive-dialog-title">WARNING</DialogTitle>
				<DialogContent>
					<DialogContentText style={{ padding: 0, margin: 0 }}>
						Be careful, once deleted you will not be able to recover this
						template, are you sure you want to delete it?
					</DialogContentText>
				</DialogContent>
				<DialogActions style={{ padding: "16px 24px" }}>
					<Button
						onClick={() => setopenModalDelete(false)}
						autoFocus
						color="inherit"
					>
						CANCEL
					</Button>
					<Button
						autoFocus
						color="secondary"
						onClick={() => deleteDetail(templateIdToDelete!)}
					>
						Delete
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog open={onDemandModal} aria-labelledby="responsive-dialog-title">
				<DialogTitle id="responsive-dialog-title">NEW WORK ORDER</DialogTitle>
				<DialogContent style={{ padding: "0 24px" }}>
					<Grid2 container style={{ width: 400 }}>
						<Grid2 size={{ md: 12, xs: 12 }}>
							Title: {rowSelectedTemplate.Title}
						</Grid2>
						<Grid2 size={{ md: 12, xs: 12 }}>
							Asset: {rowSelectedTemplate.AssetName}
						</Grid2>
					</Grid2>
					<DialogContentText style={{ padding: 0, marginTop: 10 }}>
						Are you sure you want open a new Work Order?
					</DialogContentText>
				</DialogContent>
				<DialogActions style={{ padding: "0px 24px 18px" }}>
					<Button
						onClick={() => setOnDemandModal(false)}
						autoFocus
						color="inherit"
					>
						CANCEL
					</Button>
					<Button
						autoFocus
						color="primary"
						onClick={handleCreatePMorder}
						variant="contained"
					>
						OPEN
					</Button>
				</DialogActions>
			</Dialog>

			<Snackbar
				open={deleteSuccess}
				autoHideDuration={2500}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity="error" onClose={handleClose}>
					Deleted
				</Alert>
			</Snackbar>

			<ErrorModal error={error} onHide={() => setError("")} />
			<ErrorModal
				error={parametersError}
				onHide={() => setParametersError("")}
			/>
			<ErrorModal
				error={createPMOrderError}
				onHide={() => setCreatePMOrderError("")}
			/>
			<ErrorModal
				error={deleteTemplateError}
				onHide={() => setDeleteTemplateError("")}
			/>
		</>
	);
};
